import { storeFromArea } from '../../utils';
import { AreaAction } from '../../ReducersTypes';
import { Dispatch } from 'redux';

export const REQUEST = 'list.simple.request';
export const RESPONSE = 'list.simple.response';
export const FILTER = 'list.simple.filter';
export const INVALIDATE = 'list.simple.invalidate';
export const SELECT_PARENT = 'list.simple.selectParent';

export interface SimpleListAction<T> extends AreaAction {
  rows?: Array<T>;
  count?: number;
  filter?: any;
  parent?: any;
  dataKey?: any;
}

/**
 * Zneplatnění dat.
 * @param area oblast
 * @returns {{type: string, area: *}}
 */
export function invalidate(area: string): SimpleListAction<any> {
  return {
    type: INVALIDATE,
    area
  };
}

/**
 * Filtrování položek seznamu.
 * @param area oblast
 * @param filter filtr
 * @returns {{type: string, area: *, filter: *}}
 */
export function filter(area: string, filter: any): SimpleListAction<any> {
  return {
    type: FILTER,
    area,
    filter
  };
}

/**
 * Načtení dat pokud je potřeba.
 * @param area oblast
 * @param parent parent data pro seznam, pokud je potřeba, např. id nadřazené entity, ke které se vztahuje seznam
 * @param getData promise pro načtení
 * @param forceFetch pokud je true, provede se vždy nové načtení
 */
export function fetchIfNeeded(
  area: string,
  parent: any,
  getData: (parent: any, filter: any) => Promise<any>,
  forceFetch = false
) {
  return (dispatch: Dispatch, getState: Function) => {
    // Spočtení data key
    let store = storeFromArea(getState(), area);

    // Pokud ve store není potřebné parent, dáme ho tam, aby se nám správně spočetl data key
    if (JSON.stringify(store.parent) !== JSON.stringify(parent)) {
      dispatch(selectParent(area, parent));
      store = storeFromArea(getState(), area);
    }

    const dataKey = store.getDataKey.bind(store)();
    if (forceFetch || store.currentDataKey !== dataKey) {
      dispatch(request(area, dataKey));

      const filter = store.filter;
      getData(parent, filter).then(json => {
        const newStore = storeFromArea(getState(), area);
        const newDataKey = newStore.getDataKey.bind(newStore)();
        if (newDataKey === dataKey) {
          // jen pokud přídchozí objekt odpovídá datům, které chceme ve store
          if (json && json.rows) {
            dispatch(response(area, json.rows, json.count));
          } else if (json) {
            dispatch(response(area, json, 0));
          }
        }
      }); // TODO [stanekpa] - řešit catch a vrácení datakey!
    }
  };
}

/**
 * Test, předaná akce je akcí pro tuto třídu.
 * @param action akce
 * @returns {boolean} true, pokud action odpovídá této třídě
 */
export function is(action: SimpleListAction<any>) {
  switch (action.type) {
    case FILTER:
    case REQUEST:
    case RESPONSE:
    case INVALIDATE:
      return true;
    default:
      return false;
  }
}

/**
 * Výběr nového parenta, volá se i v případě, že je zavoláno fetchIfNeeded s jiným parent něž ve store.
 * @param area oblast
 * @param parent parent id
 * @returns {{type: string, area: *, id: *}}
 */
function selectParent(area: string, parent: any): SimpleListAction<any> {
  return {
    type: SELECT_PARENT,
    area,
    parent
  };
}

/**
 * Request dat.
 * @param area oblast
 * @param dataKey data key
 * @returns {{type: string, area: *, dataKey: *}}
 */
function request(area: string, dataKey: any): SimpleListAction<any> {
  return {
    type: REQUEST,
    area,
    dataKey
  };
}

/**
 * Response dat.
 * @param area oblast
 * @param rows řádky
 * @param count celkový počet řádek, např. v db pro daný filter
 * @returns {{type: string, area: *, data: *}}
 */
function response(
  area: string,
  rows: Array<any>,
  count?: number
): SimpleListAction<any> {
  return {
    type: RESPONSE,
    area,
    rows,
    count
  };
}

/**
 * Nastavení konkrétních dat předanými, nastavením se aktualizuje i stav fetched a isFetching tak, aby byl výsledek stejný jako po reálném načtení dat ze serveru
 * @param area oblast
 * @param parent parent id
 * @param rows data
 */
export function setData(area: string, parent: any, rows: any) {
  return (dispatch: Dispatch, getState: Function) => {
    // Načtení aktuálního store
    let store = storeFromArea(getState(), area);

    // Pokud ve store není potřebné id, dáme ho tam, aby se nám správně spočetl data key
    if (store.parent !== parent) {
      dispatch(selectParent(area, parent));
      store = storeFromArea(getState(), area);
    }

    const dataKey = store.getDataKey.bind(store)();

    // Simulace request dat
    dispatch(request(area, dataKey));

    // Response dat
    dispatch(response(area, rows));
  };
}
